// Maker-side detail for the Forms and Parts explainers: the maker's brand colour, product photo,
// the document their form is printed as, who signs it, and the fault/part story. All names are fictional.
import type { Unit } from './unit';

type Extra = { accent?: string; product?: string; key?: string };
const extra = (u: Unit) => u as Unit & Extra;

/** Brand colours for the fictional makers that have no HeroUnit accent. */
const ACCENTS: Record<string, string> = {
  Harrowfield: '#2E4A3E',
  Talbrook: '#3A3A3A',
  Fornell: '#8A5A2B',
  Lotwerk: '#2F4A6B',
  'Aldwyn Audio': '#5B3A29',
  Halvard: '#2E6F6A',
  Moorcroft: '#2F5A3A',
  Velora: '#33506B',
  Brennock: '#0F6E8C',
  Strideworth: '#D4462A',
};

export const accentOf = (u: Unit) => extra(u).accent ?? ACCENTS[u.brand] ?? '#2E6F6A';

/** Monogram in the middle of the maker's code: the first letter of their name. */
export const markOf = (u: Unit) => u.brand.trim().charAt(0).toUpperCase();

export const PRODUCT_FALLBACK = '/images/v2/gen/heatpump-commission-b.jpg';
/** Catalogue photo when the unit has one (HeroUnits). Never drawn. */
export const productOf = (u: Unit): string | undefined => extra(u).product;

/** The paper document the form is printed as today. */
const DOCS: Record<string, string> = {
  Commissioning: 'Commissioning certificate',
  'Thorough examination': 'Thorough examination report',
  'Pre-delivery inspection': 'Pre-delivery inspection',
  'Installation check': 'Gas safety and installation record',
  'Installation sign-off': 'Installation sign-off',
  'Goods-in check': 'Certificate of conformity',
  'Setup and registration': 'Setup and handover record',
  'PPM and calibration': 'PPM and calibration certificate',
  'Service and safety inspection': 'Service and safety inspection record',
};
export const docTitleOf = (u: Unit) => DOCS[u.form] ?? u.form;

/** Who signs: the trade partner (or competent person) and the owner. */
export function signersOf(u: Unit): [{ role: string; name: string }, { role: string; name: string }] {
  const first = /owner/i.test(u.signLabel) ? u.Role : u.signLabel;
  const second = /homeowner/i.test(u.signLabel) ? 'Homeowner' : u.partnerRole === 'service agent' && /hire/i.test(u.owner) ? 'Duty holder' : 'Owner';
  return [
    { role: first, name: u.partner },
    { role: second, name: u.ownerShort },
  ];
}

/** Two-letter form reference printed on the paper original. */
export const formRef = (u: Unit) =>
  `${u.brand
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()}-${docTitleOf(u)
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 3)} rev 4`;

/* ---------------- parts story ---------------- */

export type PartStory = {
  q: string;
  a: string;
  source: string;
  part: string;
  price: string;
  consumable: string;
  consumablePrice: string;
  dueLine: string;
  dueTitle: string;
};

const STORIES: Record<string, Partial<PartStory>> = {
  heatpump: {
    q: 'It is showing E21 and the fan has stopped.',
    a: 'E21 on the Halvard AW-9 means the fan motor has stalled. Switch to defrost and wait 10 minutes. If it comes back, the fan motor needs replacing.',
    source: 'Installation manual p.42',
    part: 'Fan motor assembly',
    price: '£86.40',
    consumable: 'System filter cartridge',
    consumablePrice: '£18.50',
    dueLine: 'Your annual service is coming up. Fit a fresh system filter cartridge.',
    dueTitle: 'Filters due',
  },
  mower: {
    q: 'It will not start on the first cut of spring.',
    a: 'On the Moorcroft RM-42 the engine only cranks with someone in the seat. Check the seat safety switch, then the battery. A flat battery after winter is the usual cause; this one fits your mower.',
    source: "Owner's manual p.24",
    part: 'Starter battery',
    price: '£54.00',
    consumable: 'Blades and spring service kit',
    consumablePrice: '£46.50',
    dueLine: 'The spring service is due. Fresh blades and a service kit for your mower are ready.',
    dueTitle: 'Service due',
  },
  oven: {
    q: 'No steam, and the steam light is flashing.',
    a: 'On the Fornell CX-10 that means the steam generator element has failed its self-test. Your service partner can fit a new element.',
    source: 'Service manual p.36',
    part: 'Steam generator element',
    price: '£148.00',
    consumable: 'Descaler cartridges',
    consumablePrice: '£32.00',
    dueLine: 'Descaling is due after 200 hours in hard-water areas.',
    dueTitle: 'Descaling due',
  },
  ebike: {
    q: 'The range has dropped a lot since last year.',
    a: 'On the Velora T8 some range loss in year two is normal, and more in the cold. A battery health check shows how much capacity your battery still holds and whether the warranty covers it.',
    source: 'Battery manual p.9',
    part: 'Battery health check',
    price: '£35.00',
    consumable: 'Brake pads and chain',
    consumablePrice: '£42.00',
    dueLine: 'Your annual service is due. Brake pads and a chain for your bike are ready.',
    dueTitle: 'Service due',
  },
};

export function partStoryOf(u: Unit): PartStory {
  const s = STORIES[extra(u).key ?? ''] ?? {};
  return {
    q: s.q ?? `${u.issue}. What do I do?`,
    a: s.a ?? `On the ${u.model} that usually points to the ${u.part.toLowerCase()}. Here is the one that fits your unit.`,
    source: s.source ?? u.source,
    part: s.part ?? u.part,
    price: s.price ?? '£64.00',
    consumable: s.consumable ?? 'Service kit',
    consumablePrice: s.consumablePrice ?? '£24.00',
    dueLine: s.dueLine ?? 'Your next service is coming up. The kit for your unit is ready.',
    dueTitle: s.dueTitle ?? 'Service due',
  };
}
